/* rr-guide-drawer.js - the size guide, as a tabbed drawer.

   WHY THIS IS A MOVER, NOT A REBUILD
   sections/rr-fit-finder.liquid is 1,952 lines and its logic is load-bearing.
   This file does NOT touch it. It builds an empty drawer, then MOVES the
   existing section node into a pane inside it. The section keeps its markup,
   settings, JS and listeners - moving a node does not detach them.

   WHY A DRAWER
   88% of sessions are mobile. Scrolling to a section three screens down loses
   the size picker and the add-to-cart. A drawer keeps the buy column
   underneath, so the size question costs one tap out and one tap back.

   THE TABS
   Two questions get asked at this moment and they are not the same question.
   "Which size am I" is arithmetic - the fit finder answers it. "What does it
   actually look like on someone my size" is not arithmetic, and no chart has
   ever answered it. The second pane is rendered server-side by
   snippets/rr-fit-models.liquid and adopted the same way, so a product with no
   fit_models simply gets one pane and no tab strip.

   THEME EDITOR
   Shopify re-renders a section into its ORIGINAL DOM position on
   shopify:section:load, so we re-adopt. Selecting it opens the drawer.
*/
(function () {
  'use strict';

  var SECTION_SEL = '[id*="fit_finder"], [id*="sizing_intel"], [id*="size_chart"]';
  var EXTRA_SEL = '[data-rr-guide-panel]';
  var OPENER_SEL = '[data-rr-size-guide]';
  var FOCUSABLE = 'a[href],button:not([disabled]),input:not([disabled]),select:not([disabled]),textarea:not([disabled]),[tabindex]:not([tabindex="-1"])';

  var drawer, panel, host, bar, tabstrip;
  var panes = [];
  var active = 0;
  var lastFocus;

  function build() {
    drawer = document.createElement('div');
    drawer.className = 'rr-gd';
    drawer.setAttribute('hidden', '');
    drawer.innerHTML =
      '<div class="rr-gd__scrim" data-gd-close></div>' +
      '<div class="rr-gd__panel" role="dialog" aria-modal="true" aria-label="Size guide" tabindex="-1">' +
        '<div class="rr-gd__bar">' +
          '<div class="rr-gd__tabs" role="tablist"></div>' +
          '<button type="button" class="rr-gd__close" data-gd-close aria-label="Close size guide">' +
            '<svg viewBox="0 0 20 20" width="16" height="16" aria-hidden="true" focusable="false">' +
            '<path d="M3 3 L17 17 M17 3 L3 17" stroke="currentColor" stroke-width="1.6" fill="none"/></svg>' +
          '</button>' +
        '</div>' +
        '<div class="rr-gd__body"></div>' +
      '</div>';
    document.body.appendChild(drawer);
    panel = drawer.querySelector('.rr-gd__panel');
    host = drawer.querySelector('.rr-gd__body');
    bar = drawer.querySelector('.rr-gd__bar');
    tabstrip = drawer.querySelector('.rr-gd__tabs');

    drawer.addEventListener('click', function (e) {
      var t = e.target;
      while (t && t !== drawer) {
        if (t.hasAttribute && t.hasAttribute('data-gd-close')) { close(); return; }
        t = t.parentNode;
      }
    });

    document.addEventListener('keydown', function (e) {
      if (drawer.hasAttribute('hidden')) return;
      if (e.key === 'Escape') { close(); return; }
      if (e.key !== 'Tab') return;
      var items = panel.querySelectorAll(FOCUSABLE);
      if (!items.length) return;
      var first = items[0], last = items[items.length - 1];
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    });
  }

  /* One pane per adopted node. The pane is ours, the node inside it is not -
     which is what keeps the show/hide off the section's own classes. */
  function pane(label) {
    var el = document.createElement('div');
    el.className = 'rr-gd__pane';
    el.setAttribute('role', 'tabpanel');
    host.appendChild(el);
    var rec = { label: label, el: el, node: null };
    panes.push(rec);
    return rec;
  }

  function adopt() {
    var found = document.querySelector(SECTION_SEL);
    if (found) {
      var rec = panes[0] || pane('Fit finder');
      if (found.parentNode !== rec.el) rec.el.appendChild(found);
      rec.node = found;
    }

    var extra = document.querySelector(EXTRA_SEL);
    if (extra && !extra.closest('.rr-gd__pane')) {
      var label = extra.getAttribute('data-rr-guide-label') || 'How it fits';
      var rec2 = pane(label);
      rec2.el.appendChild(extra);
      rec2.node = extra;
      extra.removeAttribute('hidden');
    }

    return panes.length > 0 && !!panes[0].node;
  }

  function buildTabs() {
    tabstrip.innerHTML = '';
    if (panes.length < 2) {
      var title = document.createElement('span');
      title.className = 'rr-gd__title';
      title.textContent = 'Size guide';
      tabstrip.appendChild(title);
      return;
    }
    panes.forEach(function (p, i) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'rr-gd__tab';
      b.setAttribute('role', 'tab');
      b.textContent = p.label;
      b.addEventListener('click', function () { select(i); });
      tabstrip.appendChild(b);
      p.tab = b;
    });
  }

  function select(i) {
    active = i;
    panes.forEach(function (p, j) {
      var on = j === i;
      p.el.classList.toggle('is-on', on);
      if (p.tab) p.tab.setAttribute('aria-selected', on ? 'true' : 'false');
    });
    host.scrollTop = 0;
  }

  function open() {
    if (!panes.length) return;
    lastFocus = document.activeElement;
    drawer.removeAttribute('hidden');
    document.documentElement.classList.add('rr-gd-open');
    requestAnimationFrame(function () {
      drawer.classList.add('is-open');
      panel.focus();
    });
  }

  function close() {
    drawer.classList.remove('is-open');
    document.documentElement.classList.remove('rr-gd-open');
    var done = function () { drawer.setAttribute('hidden', ''); };
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) done();
    else setTimeout(done, 260);
    if (lastFocus && lastFocus.focus) lastFocus.focus();
  }

  function wireOpeners() {
    var buttons = document.querySelectorAll(OPENER_SEL);
    for (var i = 0; i < buttons.length; i++) {
      var b = buttons[i];
      if (b.dataset.gdWired) continue;
      b.dataset.gdWired = '1';
      b.hidden = false;
      b.setAttribute('aria-haspopup', 'dialog');
      b.addEventListener('click', function () { open(); });
    }
  }

  function init() {
    if (!drawer) build();
    if (!adopt()) return;
    buildTabs();
    select(Math.min(active, panes.length - 1));
    wireOpeners();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function () { setTimeout(init, 0); });
  document.addEventListener('shopify:section:select', function (e) {
    if (panes[0] && panes[0].node && (e.target === panes[0].node || panes[0].node.contains(e.target))) {
      select(0);
      open();
    }
  });
})();
